import { Button, Divider } from 'antd';
import { Parameter } from 'api';
import ParameterCard from 'components/parameter-card/parameter-card';
import Typography from 'helpers/fonts';
import { formatMoney } from 'helpers/util';
import React, { RefObject, useRef } from 'react';
import { useAppState } from 'state/appstate/appstate';
import { useUiState } from 'state/ui-state/ui-state';
import { HEADER_HEIGHT } from 'helpers/constants';
import { useSmallScreen } from 'helpers/isSmallScreen';
import { useElementHeight } from 'helpers/useElementHeight';
import { useTranslation } from 'react-i18next';
import { motion } from 'framer-motion';
import { removeOption, updateOptionParameters } from 'viewer/viewerActions';
import { TabPaneFeatures } from './tabpane-features';
import { useOnClickOutside } from 'helpers/useOnClickOutside';
import { CloseOutlined } from '@ant-design/icons';

const OptionParametersSidebar: React.FunctionComponent = () => {
  const isSmallScreen = useSmallScreen();
  const { t } = useTranslation();
  let ref: RefObject<HTMLDivElement> = useRef(null);
  const runningTotalHeight = useElementHeight(ref);
  const maxHeight = isSmallScreen
    ? 'auto'
    : HEADER_HEIGHT + (runningTotalHeight || 0);

  const { activeOption, setActiveOption } = useUiState();
  const { options, optionTypes } = useAppState((state) => ({
    optionTypes: state.styleProduct.style?.optionTypes,
    options: state.styleProduct.style?.options,
  }));
  const option = options?.find((ot) => ot.orderCode === activeOption?.optionId);
  const ghModelJson = option?.ghModelJson && JSON.parse(option?.ghModelJson);
  const parameters = optionTypes
    ?.find((o) => o.id === option?.optionTypeId)
    ?.parameters?.filter(
      ({ name }) =>
        name !== 'Door Opening' || ghModelJson?.doorOpening === undefined
    );
  const rootComponentRef = useRef(null);
  useOnClickOutside(rootComponentRef, () => {
    if (isSmallScreen) {
      return;
    }
    setActiveOption(undefined);
  });

  return (
    <motion.div
      ref={rootComponentRef}
      style={{
        display: 'flex',
        flexDirection: 'column',
        width: '100%',
        height: '100%',
        background: Theme['@grey-lightest'],
        position: 'relative',
        zIndex: 5,
      }}
      initial={{ opacity: 0, translateX: 50 }}
      animate={{ opacity: 1, translateX: 0, transition: { duration: 0.4 } }}
      key={'ops'}
    >
      {activeOption && option && (
        <div
          style={{
            maxHeight: `calc(${document.documentElement.style.getPropertyValue(
              '--app-height'
            )} - ${maxHeight}px)`,
            padding: 18,
          }}
          className="invisible-scroll"
        >
          <div
            style={{
              marginBottom: 12,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between',
              flexWrap: 'nowrap',
            }}
          >
            <div>
              <Typography styleid={8}>{option.title}</Typography>
              <Typography styleid={7}> ({option.orderCode})</Typography>
            </div>

            <Button
              type="text"
              size={'large'}
              onClick={() => {
                setActiveOption(undefined);
              }}
            >
              <CloseOutlined style={{ color: Theme['@black'] }} />
            </Button>
          </div>
          <div style={{ marginBottom: 12 }}>
            <Typography styleid={9}>{option.description}</Typography>
          </div>
          {option.boxSize && (
            <div style={{ marginBottom: 12 }}>
              <Typography styleid={9}>Size: {option.boxSize}</Typography>
            </div>
          )}
          <div>
            <Typography styleid={10}>
              {formatMoney(option.pricePerUnit)}
            </Typography>
          </div>

          {activeOption.unDeletable !== true && (
            <Button
              style={{ marginTop: 20 }}
              type="primary"
              onClick={() => {
                removeOption(activeOption.id);
                setActiveOption(undefined);
              }}
            >
              {t('options.delete')}
            </Button>
          )}

          <Divider
            style={{
              borderColor: `${Theme['@grey-normal']}`,
              margin: '30px 0',
            }}
          />
          {parameters &&
            parameters
              .filter((p) => option.orderCode !== 'D3S' || p.key !== 'mirror')
              .map((parameter: Parameter) => (
                <div style={{ marginBottom: 40 }} key={parameter.key}>
                  <ParameterCard
                    parameter={parameter}
                    onChange={(value) => {
                      updateOptionParameters(
                        activeOption.id,
                        parameter.key,
                        value
                      );
                    }}
                  />
                </div>
              ))}

          <TabPaneFeatures
            optionInstance={activeOption}
            option={option}
            featuresType={
              option.optionTypeId === 'windows'
                ? 'window_features'
                : option.optionTypeId === 'doors'
                ? 'door_features'
                : ''
            }
          />
        </div>
      )}
    </motion.div>
  );
};

export default OptionParametersSidebar;
